/**
 * 全編モーションの生成前に、ブランド設定で欠けているもの (モーションの出来に影響するもの) を列挙する
 * (docs/features/brand-cut-motion-graphics-plan.md §10.10.4・利用者指示 2026-10-05「欠けているものは列挙したい」)。
 *
 * backend は欠けていても生成を止めず代替で作る (ロゴから色を推定・無彩色・効果音だけ等) ので、
 * ここでは「何が欠けていて、代わりにどう作るか・どこで設定できるか」を 1 行ずつ出す。
 * ブランドの読み込み中・取得失敗は空 (注意を出さない)。
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../../services/apiClient';
import { useScenarioProductBrandInfo } from '../../../../features/scenarios/hooks';
import { BRAND_COLOR_FALLBACK_NOTICE, isBrandColorUnset, normalizeDesignLanguage, useBrandDetail } from '../../../../features/brands/designLanguage';
import type { Brand } from '../../../../features/brands/types';

export const BRAND_MISSING_NOTICE = 'ブランド未設定: ロゴやブランドカラー無しの汎用的な配色で作ります (商品にブランドを紐づけると反映されます)';
export const LOGO_MISSING_NOTICE = 'ロゴ未登録: ロゴ無しで作ります (ブランド管理で登録できます)';
export const BRAND_COLOR_UNSET_NO_LOGO_NOTICE = 'ブランドカラー未設定: 落ち着いた無彩色にアクセント1色で作ります (ブランド管理で設定できます)';
export const DESIGN_LANGUAGE_MISSING_NOTICE = 'トーン＆マナー未設定: ブランドカラーだけを手がかりに作ります (ブランド管理のトーン＆マナーでサイトから読み取れます)';
export const SOUND_LOGO_MISSING_NOTICE = 'サウンドロゴ未登録: 効果音だけで作ります (ブランド管理で登録できます)';

type BrandForGaps = Pick<Brand, 'brand_main_color' | 'brand_sub_color' | 'brand_accent_color' | 'design_language' | 'logos'>;

/**
 * 欠けているものの一覧 (表示順)。
 * - hasBrand=false (商品にブランドが無い) → ブランド未設定の 1 行だけ
 * - brand が未取得 (null) → 空 (読み込み中は出さない)
 * - soundLogoCount が null (未取得) → サウンドロゴの行は出さない
 */
export function motionBrandGaps(input: {
  hasBrand: boolean;
  brand: BrandForGaps | null | undefined;
  soundLogoCount: number | null | undefined;
}): string[] {
  if (!input.hasBrand) return [BRAND_MISSING_NOTICE];
  const brand = input.brand;
  if (!brand) return [];
  const gaps: string[] = [];
  const hasLogo = (brand.logos?.length ?? 0) > 0;
  if (!hasLogo) gaps.push(LOGO_MISSING_NOTICE);
  if (isBrandColorUnset(brand)) {
    gaps.push(hasLogo ? BRAND_COLOR_FALLBACK_NOTICE : BRAND_COLOR_UNSET_NO_LOGO_NOTICE);
  } else if (!normalizeDesignLanguage(brand.design_language)) {
    gaps.push(DESIGN_LANGUAGE_MISSING_NOTICE);
  }
  if (input.soundLogoCount === 0) gaps.push(SOUND_LOGO_MISSING_NOTICE);
  return gaps;
}

export function useScenarioMotionBrandGaps(scenarioId: string | null | undefined, enabled = true): string[] {
  const { data: brandInfo } = useScenarioProductBrandInfo(enabled && scenarioId ? scenarioId : null);
  const brandId = brandInfo?.current_brand_id ?? null;
  const { data: brand } = useBrandDetail(brandId, enabled);
  const { data: soundLogos } = useQuery({
    queryKey: ['brand-sound-logos', brandId],
    queryFn: async () => {
      const resp = await apiClient.get<{ sound_logos?: unknown[] }>(`/brands/${brandId}/sound-logos`);
      return resp.data.sound_logos ?? [];
    },
    enabled: enabled && !!brandId,
    staleTime: 60 * 1000,
  });
  if (!brandInfo) return [];
  return motionBrandGaps({ hasBrand: !!brandId, brand, soundLogoCount: soundLogos ? soundLogos.length : null });
}
